import { useCallback } from "react";

import { nameToPathMap } from "features/data-source/data-source-registry";
import { shouldUseJSBE, shouldUseNewApproach } from "features/data-source/data-source.utils";
import { useHasUserArea } from "hooks/useHasUserArea";
import { InvalidateCacheQuery } from "store/hooks/data-source-cache.types";
import { getCacheArgs } from "store/hooks/data-source-cache.utils";
import { dataSourceApi, TAG_DATA_SOURCE } from "store/services/data-source.service";
import {
    dataSourceApi as dataSourceApiDeprecated,
    TAG_DATA_SOURCE as TAG_DATA_SOURCE_DEPRECATED,
} from "store/services/deprecated/data-source.service";
import { useAppDispatch } from "store/store.hooks";

type InvalidateCacheOptions = {
    query?: InvalidateCacheQuery;
};

const useInvalidateDataSourceCache = () => {
    const dispatch = useAppDispatch();
    const hasJSBEDataSourcesEnabled = useHasUserArea(["Refactor_JSBEDataSources"]);
    const hasDataSourcesNewApproachEnabled = useHasUserArea(["Refactor_DataSourcesNewApproach"]);

    /**
     * Invalidates the data source cache.
     *
     * @param options.query When provided, only the cache for the specified query will be invalidated.
     */
    const invalidateCache = useCallback(
        (options?: InvalidateCacheOptions) => {
            const { query } = options ?? {};

            const useJSBE = query ? shouldUseJSBE(query.name, hasJSBEDataSourcesEnabled) : false;
            const useNewApproach = query
                ? shouldUseNewApproach(nameToPathMap[query.name], hasDataSourcesNewApproachEnabled)
                : false;

            let id: string | undefined;
            if (query) {
                const args = getCacheArgs(query, {
                    shouldUseNewApproach: useNewApproach,
                    shouldUseJSBE: useJSBE,
                });
                id = JSON.stringify(args);
            }

            if (useNewApproach || useJSBE) {
                dispatch(dataSourceApi.util.invalidateTags([{ type: TAG_DATA_SOURCE, id }]));
            } else {
                dispatch(dataSourceApiDeprecated.util.invalidateTags([{ type: TAG_DATA_SOURCE_DEPRECATED, id }]));
            }
        },
        [dispatch, hasDataSourcesNewApproachEnabled, hasJSBEDataSourcesEnabled]
    );

    return invalidateCache;
};

export { useInvalidateDataSourceCache };
